import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import {AbstractControl, FormControl, FormGroup, Validators} from "@angular/forms";
import { Router } from '@angular/router';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { TranslocoService } from "@ngneat/transloco";
import { ORGANISER_EDIT } from 'src/app/app-routing.module';
import { AbstractComponent } from "@components";
import {
  ApplicationProcedureTypes,
  Category, DistanceMarkers,
  EventItem, FetchRaceByIdRequest,
  GenderSeparationTypes,
  Lookups,
  PenaltyMinutesTypes,
  Race,
  RaceSettings, RegistrationDisplayFlags, sportsTypeVisibilityMap, SportType, SportTypeFieldsVisibility,
  TeamRankingTeamSeparationTypes,
  TimingSystem
} from "@model";
import { LoaderService, RaceService, RaceSettingsService, TOAST_STATE, ToastService } from "@services";
import { required } from "@utility";
import { TeamType } from 'src/app/model/team-type';
import { take } from "rxjs";

@Component({
  selector: 'app-race-details-settings',
  templateUrl: './race-details-settings.component.html',
  styleUrls: ['./race-details-settings.component.scss']
})
export class RaceDetailsSettingsComponent extends AbstractComponent implements OnInit {
  @Input() raceSettings: RaceSettings = new RaceSettings();
  @Input() event?: EventItem;
  @Input() race?: Race;
  @Input() disableEditing = false;
  @Input() categoryList: Category[] = [];
  @Input() applicationProcedureTypes: Lookups[] = [];
  @Input() typeOfApplicationTypes: Lookups[] = [];
  @Input() teamTypes: Lookups[] = [];
  @Input() selectionOfTeamMembersTypes: Lookups[] = [];
  @Input() measurementTypes: Lookups[] = [];
  @Input() measurementPrecisionTypes: Lookups[] = [];
  @Input() startByTypeTypes: Lookups[] = [];
  @Input() genderSeparationTypes: Lookups[] = [];
  @Input() teamRankingTeamSeparationTypes: Lookups[] = [];
  @Input() rotatingOrderTypes: any[] = [];
  @Input() timingSystems: Lookups[] = [];
  @Input() timingEquipments: Lookups[] = [];
  @Input() penaltyMinutesTypes: Lookups[] = [];
  @Output() raceChange = new EventEmitter<Race>();

  @ViewChild('categorySelect') dropdown?: NgbDropdown;

  inputForm = new FormGroup({
    applicationProcedure: new FormControl(),
    typeOfApplication: new FormControl(),
    teamType: new FormControl(),
    selectionOfTeamMembersType: new FormControl(),
    teamNameRequired: new FormControl(),
    genderRequired: new FormControl(),
    measurementType: new FormControl(),
    measurementPrecisionType: new FormControl(),
    startByType: new FormControl(),
    resultsPerPage: new FormControl(),
    topRanked: new FormControl(),
    teamParticipantsForRanking: new FormControl(),
    teamRankingGenderSeparation: new FormControl(),
    rankingGroups: new FormControl(),
    participantsFromRankingGroup: new FormControl(),
    groupRankingSeparation: new FormControl(),
    teamRankingTeamSeparation: new FormControl(),
    penaltyMinutes: new FormControl(),
    controlPoints: new FormControl(),
    roundRace: new FormControl(),
    minimumLapDuration: new FormControl(),
    rotatingOrder: new FormControl(),
    rotatingResultsDisplay: new FormControl(),
    rotatingResultsSeconds: new FormControl(),
    pic2GoEventId: new FormControl(),
    pic2GoEventUniqueCode: new FormControl(),
    registrationExternalUrl: new FormControl(),
    category: new FormControl(),
    maxGroupRegisteredParticipants: new FormControl(),
    useGroupRanking: new FormControl(),
    timingSystem: new FormControl(),
    timingEquipment: new FormControl(),
    enableGroupRegistrationPayment: new FormControl(),
    usingUploadRoute: new FormControl(),
    displayControlPoints: new FormControl(),
    showRouteProfile: new FormControl(),
    enableGPXDownload: new FormControl(),
    usingDistanceMarkersOnRoute: new FormControl(),
    distanceMarkersOnRoute: new FormControl(),
    maxParticipantsInStartBlock: new FormControl(),
    blockStart: new FormControl()
  });

  distanceMarkers = new Map<number, string>();

  constructor(
    public translocoService: TranslocoService,
    private raceSettingsService: RaceSettingsService,
    private toastService: ToastService,
    private router: Router,
    private loaderService: LoaderService,
    private raceService: RaceService
  ) {
    super();
  }

  ngOnInit() {
    this.subscribeToValueChanges();
    this.initForm();
    this.raceSettingsChanged();
    this.inputForm.updateValueAndValidity()
    this.distanceMarkers = this.getDistanceMarkers();
  }

  private raceSettingsChanged = () => {
    this.initDefaultFields();
    if (this.raceSettings) {
      this.inputForm.patchValue(this.raceSettings);
      if ((!this.raceSettings.minimumLapDuration || this.raceSettings.minimumLapDuration === "0") || this.race?.sport === SportType.BACKYARD_ULTRA) {
        this.disableField(this.inputForm.controls.minimumLapDuration, "0");
      } else {
        this.inputForm.controls.roundRace.setValue(true);
        this.enableField(this.inputForm.controls.minimumLapDuration, true, this.raceSettings.minimumLapDuration);
        this.inputForm.controls.minimumLapDuration.addValidators(Validators.min(1));
      }
      if (this.race?.sport === SportType.BACKYARD_ULTRA) {
        this.inputForm.controls.roundRace.setValue(true);
        this.inputForm.controls.roundRace.disable();
      }
      if (this.raceSettings.rankingGroups && this.raceSettings.useGroupRanking) {
        let rankingGroups = this.raceSettings.rankingGroups;
        if (rankingGroups.endsWith(";")) {
          rankingGroups = rankingGroups.slice(0, -1);
        }
        this.inputForm.controls.rankingGroups.setValue(rankingGroups.split(";"));
      }
      this.inputForm.controls.category.setValue(this.raceSettings.category?.id);
    }
    if (this.race?.sport) {
      if (Object.values(SportType).includes(this.race.sport)) {
        const sportTypeFieldsVisibility: SportTypeFieldsVisibility = sportsTypeVisibilityMap[this.race.sport as SportType];
        if (sportTypeFieldsVisibility.maxParticipantsInStartBlock || sportTypeFieldsVisibility.maxParticipantsInStartBlock === 0) {
          this.inputForm.controls.blockStart.enable();
        }
        else {
          this.inputForm.controls.blockStart.setValue(null);
          this.inputForm.controls.blockStart.disable();
          this.inputForm.controls.maxParticipantsInStartBlock.setValue(null);
          this.inputForm.controls.maxParticipantsInStartBlock.disable();
        }
      }
    }
    this.inputForm.updateValueAndValidity();
  }

  private initForm = () => {
    const requiredFields = [
      'applicationProcedure', 'typeOfApplication', 'teamType', 'measurementPrecisionType',
      'measurementType', 'startByType', 'resultsPerPage', 'topRanked', 'teamRankingGenderSeparation',
      'controlPoints', 'penaltyMinutes', 'minimumLapDuration', 'rotatingOrder', 'rotatingResultsSeconds',
      'rotatingResultsDisplay', 'category', 'enableGroupRegistrationPayment', 'useGroupRanking', 'timingSystem'
    ];
    requiredFields.forEach(field => (this.inputForm.get(field) as FormControl).addValidators([required]));
    if (this.disableEditing) {
      this.inputForm.disable();
    }
    this.inputForm.controls.enableGroupRegistrationPayment.addValidators([
      (control: AbstractControl): { [key: string]: any } | null => {
        const groupRegistrationEnabled = this.event?.paymentSettings?.mandatoryPayment ?? false;
        const isUsed = control.value;

        if (!groupRegistrationEnabled && isUsed) {
          control.markAsTouched();
          return { mandatoryPaymentMissing: true };
        }
        return null;
      }
    ]);
  }

  private initDefaultFields = () => {
    if (!this.raceSettings) {
      this.raceSettings = new RaceSettings();
    }
    this.raceSettings.applicationProcedure ??= ApplicationProcedureTypes.STANDARD;
    this.raceSettings.teamRankingGenderSeparation ??= TeamRankingTeamSeparationTypes.NOT_USED;
    this.raceSettings.penaltyMinutes ??= PenaltyMinutesTypes.NOT_VISIBLE;
    this.raceSettings.useGroupRanking ??= false;
    this.raceSettings.enableGroupRegistrationPayment ??= false;
    if (this.raceSettings.usingUploadRoute === undefined || this.raceSettings.usingUploadRoute === null) {
        this.raceSettings.usingUploadRoute = true;
        this.raceSettings.displayControlPoints = true;
        this.raceSettings.showRouteProfile = true;
        this.raceSettings.enableGPXDownload = true;
      }
    this.raceSettings.blockStart ??= false;
  }

  private subscribeToValueChanges = () => {
    const controls = this.inputForm.controls;
    this.subscriptions.push(controls.teamType.valueChanges.subscribe(this.teamTypeChanged));
    this.subscriptions.push(controls.enableGroupRegistrationPayment.valueChanges.subscribe(this.enableGroupRegistrationPaymentChanged));
    this.subscriptions.push(controls.timingSystem.valueChanges.subscribe(this.timingSystemChanged));
    this.subscriptions.push(controls.useGroupRanking.valueChanges.subscribe(this.useGroupingChanged));
    this.subscriptions.push(controls.usingUploadRoute.valueChanges.subscribe(this.usingUploadRouteChanged));
    this.subscriptions.push(controls.usingDistanceMarkersOnRoute.valueChanges.subscribe(this.usingDistanceMarkersOnRouteChanged));
    this.subscriptions.push(controls.blockStart.valueChanges.subscribe(this.startBlockChanged));
    this.subscriptions.push(controls.roundRace.valueChanges.subscribe(this.roundRaceChanged));
  }

  private usingUploadRouteChanged = (isUsing: boolean) => {
    if (isUsing) {
      this.enableField(this.inputForm.controls.showRouteProfile, false, true);
      this.enableField(this.inputForm.controls.enableGPXDownload, false, true);
      this.enableField(this.inputForm.controls.usingDistanceMarkersOnRoute);
    } else {
      this.disableField(this.inputForm.controls.showRouteProfile);
      this.disableField(this.inputForm.controls.enableGPXDownload);
      this.disableField(this.inputForm.controls.usingDistanceMarkersOnRoute);
    }
  }

  private usingDistanceMarkersOnRouteChanged = (isUsed: boolean) => {
    isUsed ? this.enableField(this.inputForm.controls.distanceMarkersOnRoute) : this.disableField(this.inputForm.controls.distanceMarkersOnRoute);
  }

  private startBlockChanged = (isUsed: boolean) => {
    if (isUsed) {
      this.enableField(this.inputForm.controls.maxParticipantsInStartBlock, true);
      this.inputForm.controls.maxParticipantsInStartBlock.addValidators(Validators.min(1));
      this.inputForm.controls.maxParticipantsInStartBlock.updateValueAndValidity();
    } else {
      this.disableField(this.inputForm.controls.maxParticipantsInStartBlock);
    }
  }

  private roundRaceChanged = (isRoundRace: boolean) => {
    if (isRoundRace && this.race?.sport !== SportType.BACKYARD_ULTRA) {
      this.enableField(this.inputForm.controls.minimumLapDuration, true, "0");
      this.inputForm.controls.minimumLapDuration.addValidators(Validators.min(1));
      this.inputForm.controls.minimumLapDuration.updateValueAndValidity();
    } else {
      this.disableField(this.inputForm.controls.minimumLapDuration, "0");
    }
  }

  private teamTypeChanged = (teamType: number | null) => {
    const controls = this.inputForm.controls;
    if (teamType === TeamType.SINGLE || teamType === null) {
      this.disableField(controls.selectionOfTeamMembersType);
      this.disableField(controls.teamNameRequired);
      this.disableField(controls.genderRequired);
      this.enableField(controls.enableGroupRegistrationPayment);
      this.enableField(controls.maxGroupRegisteredParticipants);
    } else {
      this.enableField(controls.selectionOfTeamMembersType, true, RegistrationDisplayFlags.NOT_USED);
      this.enableField(controls.teamNameRequired, true, false);
      this.enableField(controls.genderRequired, true, false);
      this.disableField(controls.enableGroupRegistrationPayment);
      this.disableField(controls.maxGroupRegisteredParticipants);
    }
  }

  private enableGroupRegistrationPaymentChanged = (isUsed: boolean | null) => {
    const groupRegistrationEnabled = this.event?.paymentSettings?.mandatoryPayment ?? false;
    isUsed && groupRegistrationEnabled ? this.enableField(this.inputForm.controls.maxGroupRegisteredParticipants, true) : this.disableField(this.inputForm.controls.maxGroupRegisteredParticipants);
  }

  private timingSystemChanged = (timingSystem: number | null) => {
    timingSystem === TimingSystem.PLATFORM || timingSystem === null
      ? this.disableField(this.inputForm.controls.timingEquipment)
      : this.enableField(this.inputForm.controls.timingEquipment, true);
  }

  private useGroupingChanged = (isUsed: boolean | null) => {
    if (isUsed) {
      this.enableField(this.inputForm.controls.rankingGroups);
      this.enableField(this.inputForm.controls.participantsFromRankingGroup);
      this.enableField(this.inputForm.controls.groupRankingSeparation, true, GenderSeparationTypes.NOT_USED);
      this.enableField(this.inputForm.controls.teamRankingTeamSeparation, true, TeamRankingTeamSeparationTypes.NOT_USED);
    } else {
      this.disableField(this.inputForm.controls.rankingGroups);
      this.disableField(this.inputForm.controls.participantsFromRankingGroup);
      this.disableField(this.inputForm.controls.groupRankingSeparation);
      this.disableField(this.inputForm.controls.teamRankingTeamSeparation);
    }
  }

  private enableField = (formControl: FormControl, addRequired?: boolean, value?: any) => {
    formControl.markAsUntouched();
    formControl.enable();
    if (addRequired) formControl.addValidators([required]);
    if (value !== undefined) formControl.setValue(value);
    formControl.updateValueAndValidity();
  }

  private disableField = (formControl: FormControl, value?: any) => {
    formControl.markAsUntouched();
    formControl.removeValidators([required]);
    formControl.setValue(value ?? null);
    formControl.disable();
    formControl.updateValueAndValidity();
  }

  private getRaceSettingsFields = (): RaceSettings => {
    this.checkValidators(this.inputForm);
    let raceSettings: RaceSettings = { ...this.raceSettings };
    if (this.inputForm.controls.rankingGroups.value && raceSettings.useGroupRanking) {
      let rankingGroups: string[] = this.inputForm.controls.rankingGroups.value;
      raceSettings.rankingGroups = rankingGroups.join(";");
    } else {
      raceSettings.rankingGroups = undefined;
    }
    if (!raceSettings.raceId) raceSettings.raceId = this.race?.id;
    const controls = this.inputForm.controls;
    raceSettings.applicationProcedure = controls.applicationProcedure.value;
    raceSettings.typeOfApplication = controls.typeOfApplication.value;
    raceSettings.teamType = controls.teamType.value;
    raceSettings.selectionOfTeamMembersType = controls.selectionOfTeamMembersType.value;
    raceSettings.teamNameRequired = controls.teamNameRequired.value;
    raceSettings.genderRequired = controls.genderRequired.value;
    raceSettings.measurementType = controls.measurementType.value;
    raceSettings.measurementPrecisionType = controls.measurementPrecisionType.value;
    raceSettings.startByType = controls.startByType.value;
    raceSettings.resultsPerPage = controls.resultsPerPage.value;
    raceSettings.topRanked = controls.topRanked.value;
    raceSettings.teamParticipantsForRanking = controls.teamParticipantsForRanking.value;
    raceSettings.teamRankingGenderSeparation = controls.teamRankingGenderSeparation.value;
    raceSettings.participantsFromRankingGroup = controls.participantsFromRankingGroup.value;
    raceSettings.groupRankingSeparation = controls.groupRankingSeparation.value;
    raceSettings.teamRankingTeamSeparation = controls.teamRankingTeamSeparation.value;
    raceSettings.penaltyMinutes = controls.penaltyMinutes.value;
    raceSettings.controlPoints = controls.controlPoints.value;
    raceSettings.minimumLapDuration = controls.minimumLapDuration.value;
    raceSettings.rotatingOrder = controls.rotatingOrder.value;
    raceSettings.rotatingResultsDisplay = controls.rotatingResultsDisplay.value;
    raceSettings.rotatingResultsSeconds = controls.rotatingResultsSeconds.value;
    raceSettings.pic2GoEventId = controls.pic2GoEventId.value;
    raceSettings.pic2GoEventUniqueCode = controls.pic2GoEventUniqueCode.value;
    raceSettings.registrationExternalUrl = controls.registrationExternalUrl.value;
    raceSettings.category = this.categoryList.find(category => category.id === controls.category.value);
    raceSettings.enableGroupRegistrationPayment = controls.enableGroupRegistrationPayment.value;
    raceSettings.maxGroupRegisteredParticipants = controls.maxGroupRegisteredParticipants.value;
    raceSettings.useGroupRanking = controls.useGroupRanking.value;
    raceSettings.timingSystem = controls.timingSystem.value;
    raceSettings.timingEquipment = controls.timingEquipment.value;
    raceSettings.usingUploadRoute = controls.usingUploadRoute.value;
    raceSettings.displayControlPoints = controls.displayControlPoints.value;
    raceSettings.showRouteProfile = controls.showRouteProfile.value;
    raceSettings.enableGPXDownload = controls.enableGPXDownload.value;
    raceSettings.usingDistanceMarkersOnRoute = controls.usingDistanceMarkersOnRoute.value;
    raceSettings.distanceMarkersOnRoute = controls.distanceMarkersOnRoute.value;
    raceSettings.blockStart = controls.blockStart.value;
    raceSettings.maxParticipantsInStartBlock = controls.maxParticipantsInStartBlock.value;
    return raceSettings;
  }

  getDistanceMarkers = (): Map<number, string> => {
    const optionsMap = new Map<number, string>();
    Object.values(DistanceMarkers).forEach(distanceMarker => {
      if (typeof distanceMarker === 'number') {
        optionsMap.set(distanceMarker, this.translocoService.translate("race.settings.distanceMarkers." + distanceMarker));
      }
    });
    return optionsMap;
  }

  onSave = (): void => {
    this.markAllAsTouched(this.inputForm);
    if (!this.inputForm.valid) {
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("race.details.settings.error.form"));
      return;
    }
    if (!this.isRaceSettingsValid()) {
      return;
    }
    if (!this.race?.id) {
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("organiser.race.details.save.error.no.raceId"));
      return;
    }
    const raceSettings = this.getRaceSettingsFields();
    this.loaderService.startLoading();
    this.raceSettingsService.updateRaceSettings(raceSettings, () => {
      if (this.race?.id) {
        this.raceService.findRaceByIdProtected(new FetchRaceByIdRequest(this.race.id), false)
          .pipe(take(1)).subscribe(race => {
          this.loaderService.dismissLoading();
          this.toastService.showToast(TOAST_STATE.success, this.translocoService.translate("app.common.success.race.settings.edit"));
          this.raceChange.emit(race ?? undefined);
        });
      }
    }, (errorCode: number | undefined) => {
      this.loaderService.dismissLoading();
      let errorMessage = this.translocoService.translate("app.common.server.error");
      if (errorCode === RaceSettingsService.BLOCK_RACE_EDIT_NOT_ALLOWED) {
        errorMessage = this.translocoService.translate("race.settings.update.error", {
          field: this.translocoService.translate("race.settings.blockStart")
        })
      }
      if (errorCode === RaceSettingsService.START_BY_TYPE_EDIT_NOT_ALLOWED) {
        errorMessage = this.translocoService.translate("race.settings.update.error", {
          field: this.translocoService.translate("race.settings.startByType")
        })
      }
      this.toastService.showToast(TOAST_STATE.danger, errorMessage);
    });
  }

  onCancel = (): void => {
    this.raceSettingsChanged();
  }

  rankingGroupsChanged = (tags: string[] | undefined) => {
    this.inputForm.controls.rankingGroups.setValue(tags);
  }

  navigateToOrganiserPage = () => {
    if (this.race?.organiserId) {
      this.router.navigate([ORGANISER_EDIT], { queryParams: { organiserId: this.race.organiserId } });
    }
  }

  removeTagAtIndex = (index: number) => {
    const tags = [...this.inputForm.controls.rankingGroups.value];
    tags.splice(index, 1);
    this.inputForm.controls.rankingGroups.setValue(tags);
  }

  isRaceSettingsValid = (): boolean => {
    if (RaceSettings.isRoundRace(this.inputForm.controls.minimumLapDuration.value) && RaceSettings.isBlockRace(this.inputForm.controls.blockStart.value)) {
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("race.details.settings.error.roundRace.blockStart"));
      return false;
    } else if (RaceSettings.isRoundRace(this.inputForm.controls.minimumLapDuration.value) && RaceSettings.isStartByGenderRace(this.inputForm.controls.startByType.value)){
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("race.details.settings.error.roundRace.startByGender"));
      return false;
    } else if (RaceSettings.isRoundRace(this.inputForm.controls.minimumLapDuration.value) && Race.isChronometerRace(this.race?.startEvery)) {
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("race.details.settings.error.roundRace.startEvery"));
      return false;
    } else if (RaceSettings.isBlockRace(this.inputForm.controls.blockStart.value) && RaceSettings.isStartByGenderRace(this.inputForm.controls.startByType.value)) {
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("race.details.settings.error.blockRace.startByGender"));
      return false;
    } else if (RaceSettings.isBlockRace(this.inputForm.controls.blockStart.value) && Race.isChronometerRace(this.race?.startEvery)) {
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("race.details.settings.error.blockRace.startEvery"));
      return false;
    } else if (RaceSettings.isStartByGenderRace(this.inputForm.controls.startByType.value) && Race.isChronometerRace(this.race?.startEvery)) {
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("race.details.settings.error.startByGender.startEvery"));
      return false;
    }
    return true;
  }


  protected readonly SportType = SportType;

}
